import Search16 from '@carbon/icons-react/es/search/16';
import { useBoolean } from 'ahooks';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { components as SelectComponents } from 'react-select';
import styled from 'styled-components';
import { Avatar } from 'components/atoms/avatar';
import { Popover } from 'components/atoms/popover';
import { Select } from 'components/atoms/select';
import { VirtualizedMenuList } from 'components/atoms/select/custom-components';
import {
  Option,
  SelectComponentsConfig,
  SingleValue,
} from 'components/atoms/select/types';
import { useGetProjectIdsOfCurrentUserFromIndexedDb } from 'hooks/use-idb';
import { useTranslate } from 'hooks/use-translation';
import { getProjectSelectOptions } from '../utils';
import { TitleSelectTrigger } from './title-select-trigger';

const SelectContainer = styled.div`
  min-height: 350px;
  width: 272px;
  display: flex;
  flex-direction: column;
`;

export const DropdownIndicator = (props) => {
  return (
    <SelectComponents.DropdownIndicator {...props}>
      <Search16 />
    </SelectComponents.DropdownIndicator>
  );
};

const selectStyles = {
  control: (provided) => ({
    ...provided,
    margin: 'var(--spacing-05)',
  }),
  menu: () => ({ boxShadow: 'none' }),
};

const Trigger = ({ value }) => (
  <TitleSelectTrigger
    icon={<Avatar name={value.label} src={value.imageUrl} size={32} />}
    label={value.label}
    description={value.description}
  />
);

interface Props {
  options: Option[];
  onChange: (value: SingleValue<number>) => void;
  selected?: Option;
  triggerComponent?: ({ value }) => JSX.Element;
  components?: SelectComponentsConfig;
  className?: string;
  virtualized?: boolean;
  noOptionsMessage?: () => JSX.Element;
  selector?: string;
}

export const TitleSelect: FC<Props> = ({
  options,
  selected,
  onChange,
  triggerComponent = Trigger,
  components = {},
  className,
  virtualized = true,
  noOptionsMessage,
  selector,
}) => {
  const translate = useTranslate();
  const myProjectIds = useGetProjectIdsOfCurrentUserFromIndexedDb();
  const [visible, { toggle: toggleVisibility, setFalse: closePopover }] =
    useBoolean(false);

  const handleChange = (value: SingleValue) => {
    onChange(value);
    closePopover();
  };

  const projectSelectOptions = getProjectSelectOptions(
    options,
    myProjectIds,
    translate,
  );

  return (
    <Popover
      className={className}
      placement="bottomLeft"
      trigger={['click']}
      visible={visible}
      onVisibleChange={toggleVisibility}
      destroyTooltipOnHide
      content={
        <SelectContainer data-cy={selector || 'timesheet-select'}>
          <Select
            type="user"
            menuIsOpen
            autoFocus
            hideSelectedOptions={false}
            controlShouldRenderValue={false}
            components={{
              DropdownIndicator,
              IndicatorSeparator: () => null,
              ...(virtualized ? { MenuList: VirtualizedMenuList } : {}),
              ...components,
            }}
            styles={selectStyles}
            placeholder={<FormattedMessage id="placeholder.search" />}
            options={projectSelectOptions}
            grouped
            value={selected}
            onChange={handleChange}
            noOptionsMessage={
              noOptionsMessage ??
              (() => <FormattedMessage id="timesheets.work.select.dropdown" />)
            }
          />
        </SelectContainer>
      }
    >
      <div>
        {selected ? <>{triggerComponent({ value: selected })}</> : null}
      </div>
    </Popover>
  );
};
